/**
 * KPI options registry — performance metric definitions for template KPI configuration.
 *
 * Synced with backend kpi_registry.py (Phase 30 Plan 05).
 * Uses canonical registry IDs (e.g. video25Percent, thruPlays, videoViews).
 * Used by the template editor UI for KPI selection and by verdict rules
 * for KPI-based conditions.
 */

import type { KpiOption } from '@/types/template';

// ---------------------------------------------------------------------------
// KPI category metadata (ordered for UI display)
// ---------------------------------------------------------------------------

export interface KpiCategory {
  key: string;
  name: string;
  description: string;
}

export const KPI_CATEGORIES: KpiCategory[] = [
  {
    key: 'delivery',
    name: 'Delivery',
    description: 'Ad delivery and reach metrics: spend, impressions, reach, CPM, frequency.',
  },
  {
    key: 'traffic',
    name: 'Traffic',
    description: 'Click and traffic metrics: clicks, CTR, CPC, link clicks, landing page views.',
  },
  {
    key: 'engagement',
    name: 'Engagement',
    description: 'Post engagement metrics: reactions, saves, shares, comments.',
  },
  {
    key: 'video',
    name: 'Video',
    description: 'Video performance metrics: plays, quartile views, watch time, ThruPlays.',
  },
  {
    key: 'conversion',
    name: 'Conversion',
    description: 'Conversion and revenue metrics: purchases, ROAS, CPA, add to cart.',
  },
  {
    key: 'cost',
    name: 'Cost',
    description: 'Cost efficiency metrics: cost per ThruPlay, link click, landing page view, engagement.',
  },
  {
    key: 'derived',
    name: 'Derived',
    description: 'Calculated metrics: thumb stop ratio, hook rate, hold rate.',
  },
];

// ---------------------------------------------------------------------------
// KPI options (ordered by category, then by relevance)
// ---------------------------------------------------------------------------

export const KPI_OPTIONS: KpiOption[] = [
  // -------------------------------------------------------------------------
  // Delivery (6)
  // -------------------------------------------------------------------------
  {
    id: 'spend',
    label: 'Spend',
    shortLabel: 'Spend',
    unit: '$',
    category: 'delivery',
    higherIsBetter: false,
    description: 'Total amount spent on ad delivery during the reporting period.',
  },
  {
    id: 'impressions',
    label: 'Impressions',
    shortLabel: 'Impr.',
    unit: 'count',
    category: 'delivery',
    higherIsBetter: true,
    description: 'Total number of times the ad was shown to users.',
  },
  {
    id: 'reach',
    label: 'Reach',
    shortLabel: 'Reach',
    unit: 'count',
    category: 'delivery',
    higherIsBetter: true,
    description: 'Number of unique accounts that saw the ad at least once.',
  },
  {
    id: 'frequency',
    label: 'Frequency',
    shortLabel: 'Freq.',
    unit: 'x',
    category: 'delivery',
    higherIsBetter: false,
    description:
      'Average number of times each unique account saw the ad. ' +
      'High frequency can signal creative fatigue.',
  },
  {
    id: 'cpm',
    label: 'CPM',
    shortLabel: 'CPM',
    unit: '$/1k',
    category: 'delivery',
    higherIsBetter: false,
    description: 'Average cost to deliver 1,000 impressions. Lower CPM = more efficient delivery.',
  },
  {
    id: 'cpc',
    label: 'Cost per Click',
    shortLabel: 'CPC',
    unit: '$',
    category: 'delivery',
    higherIsBetter: false,
    description: 'Average cost for each click. Lower CPC = more cost-efficient at driving clicks.',
  },

  // -------------------------------------------------------------------------
  // Traffic (7)
  // -------------------------------------------------------------------------
  {
    id: 'clicks',
    label: 'Clicks (All)',
    shortLabel: 'Clicks',
    unit: 'count',
    category: 'traffic',
    higherIsBetter: true,
    description: 'Total clicks on any part of the ad, including reactions, comments, and shares.',
  },
  {
    id: 'ctr',
    label: 'Click-Through Rate',
    shortLabel: 'CTR',
    unit: '%',
    category: 'traffic',
    higherIsBetter: true,
    description:
      'Percentage of impressions that resulted in any click. ' +
      'Measures overall engagement with the ad unit.',
  },
  {
    id: 'linkClicks',
    label: 'Link Clicks',
    shortLabel: 'Link Clicks',
    unit: 'count',
    category: 'traffic',
    higherIsBetter: true,
    description:
      'Number of clicks on links within the ad that led to advertiser-specified destinations.',
  },
  {
    id: 'linkCtr',
    label: 'Link Click-Through Rate',
    shortLabel: 'Link CTR',
    unit: '%',
    category: 'traffic',
    higherIsBetter: true,
    description: 'Percentage of impressions that resulted in a link click to the destination URL.',
  },
  {
    id: 'outboundClicks',
    label: 'Outbound Clicks',
    shortLabel: 'Out. Clicks',
    unit: 'count',
    category: 'traffic',
    higherIsBetter: true,
    description: 'Number of clicks that lead people off Meta technologies.',
  },
  {
    id: 'outboundCtr',
    label: 'Outbound CTR',
    shortLabel: 'Out. CTR',
    unit: '%',
    category: 'traffic',
    higherIsBetter: true,
    description: 'Percentage of impressions that resulted in an outbound click.',
  },
  {
    id: 'landingPageViews',
    label: 'Landing Page Views',
    shortLabel: 'LPV',
    unit: 'count',
    category: 'traffic',
    higherIsBetter: true,
    description: 'Number of times a user loaded a landing page after clicking the ad link.',
  },

  // -------------------------------------------------------------------------
  // Engagement (6)
  // -------------------------------------------------------------------------
  {
    id: 'postEngagements',
    label: 'Post Engagements',
    shortLabel: 'Engagements',
    unit: 'count',
    category: 'engagement',
    higherIsBetter: true,
    description: 'Total post engagements including reactions, comments, shares, and clicks.',
  },
  {
    id: 'reactions',
    label: 'Reactions',
    shortLabel: 'Reactions',
    unit: 'count',
    category: 'engagement',
    higherIsBetter: true,
    description: 'Number of reactions (like, love, haha, wow, sad, angry) on the ad.',
  },
  {
    id: 'comments',
    label: 'Comments',
    shortLabel: 'Comments',
    unit: 'count',
    category: 'engagement',
    higherIsBetter: true,
    description: 'Number of comments on the ad.',
  },
  {
    id: 'shares',
    label: 'Shares',
    shortLabel: 'Shares',
    unit: 'count',
    category: 'engagement',
    higherIsBetter: true,
    description: 'Number of times users shared the ad.',
  },
  {
    id: 'engagementRate',
    label: 'Engagement Rate',
    shortLabel: 'Eng. Rate',
    unit: '%',
    category: 'engagement',
    higherIsBetter: true,
    description: 'Percentage of impressions that resulted in a post engagement action.',
  },
  {
    id: 'saves',
    label: 'Saves',
    shortLabel: 'Saves',
    unit: 'count',
    category: 'engagement',
    higherIsBetter: true,
    description: 'Number of times users saved the ad for later viewing.',
  },

  // -------------------------------------------------------------------------
  // Video (10)
  // -------------------------------------------------------------------------
  {
    id: 'videoViews',
    label: 'Video Views',
    shortLabel: 'Views',
    unit: 'count',
    category: 'video',
    higherIsBetter: true,
    description: 'Number of times the video started playing (excludes replays).',
  },
  {
    id: 'video3sViews',
    label: '3-Second Video Views',
    shortLabel: '3s Views',
    unit: 'count',
    category: 'video',
    higherIsBetter: true,
    description: 'Number of times the video was played for at least 3 seconds.',
  },
  {
    id: 'video25Percent',
    label: 'Video Views (25%)',
    shortLabel: '25% Views',
    unit: 'count',
    category: 'video',
    higherIsBetter: true,
    description: 'Number of times the video was played to 25% of its length.',
  },
  {
    id: 'video50Percent',
    label: 'Video Views (50%)',
    shortLabel: '50% Views',
    unit: 'count',
    category: 'video',
    higherIsBetter: true,
    description: 'Number of times the video was played to 50% of its length.',
  },
  {
    id: 'video75Percent',
    label: 'Video Views (75%)',
    shortLabel: '75% Views',
    unit: 'count',
    category: 'video',
    higherIsBetter: true,
    description: 'Number of times the video was played to 75% of its length.',
  },
  {
    id: 'video95Percent',
    label: 'Video Views (95%)',
    shortLabel: '95% Views',
    unit: 'count',
    category: 'video',
    higherIsBetter: true,
    description: 'Number of times the video was played to 95% of its length.',
  },
  {
    id: 'thruPlays',
    label: 'ThruPlays',
    shortLabel: 'ThruPlays',
    unit: 'count',
    category: 'video',
    higherIsBetter: true,
    description: 'Number of times the video was played to completion or for at least 15 seconds.',
  },
  {
    id: 'videoCompletionRate',
    label: 'Video Completion Rate',
    shortLabel: 'VCR',
    unit: '%',
    category: 'video',
    higherIsBetter: true,
    description: 'Percentage of video views that resulted in a ThruPlay (completion or 15s).',
  },
  {
    id: 'avgWatchTime',
    label: 'Average Watch Time',
    shortLabel: 'Avg Watch',
    unit: 's',
    category: 'video',
    higherIsBetter: true,
    description:
      'Average number of seconds viewers watched before stopping. ' +
      'Reflects creative hold strength.',
  },
  {
    id: 'thruPlayRate',
    label: 'ThruPlay Rate',
    shortLabel: 'ThruPlay %',
    unit: '%',
    category: 'video',
    higherIsBetter: true,
    description:
      'Percentage of video impressions that resulted in a ThruPlay. ' +
      'Key retention and engagement signal.',
  },

  // -------------------------------------------------------------------------
  // Conversion (8)
  // -------------------------------------------------------------------------
  {
    id: 'purchases',
    label: 'Purchases',
    shortLabel: 'Purchases',
    unit: 'count',
    category: 'conversion',
    higherIsBetter: true,
    description: 'Total number of purchase events attributed to the ad within the attribution window.',
  },
  {
    id: 'purchaseValue',
    label: 'Purchase Value',
    shortLabel: 'Purch. Value',
    unit: '$',
    category: 'conversion',
    higherIsBetter: true,
    description: 'Total revenue from purchases attributed to the ad within the attribution window.',
  },
  {
    id: 'viewContent',
    label: 'View Content',
    shortLabel: 'View Content',
    unit: 'count',
    category: 'conversion',
    higherIsBetter: true,
    description: 'Number of view content events attributed to the ad.',
  },
  {
    id: 'addToCart',
    label: 'Add to Cart',
    shortLabel: 'ATC',
    unit: 'count',
    category: 'conversion',
    higherIsBetter: true,
    description: 'Number of add-to-cart events attributed to the ad.',
  },
  {
    id: 'initiateCheckout',
    label: 'Initiate Checkout',
    shortLabel: 'Init. Checkout',
    unit: 'count',
    category: 'conversion',
    higherIsBetter: true,
    description: 'Number of initiate checkout events attributed to the ad.',
  },
  {
    id: 'cpa',
    label: 'Cost per Action',
    shortLabel: 'CPA',
    unit: '$',
    category: 'conversion',
    higherIsBetter: false,
    description: 'Average cost per landing page view. Lower CPA = more efficient conversion.',
  },
  {
    id: 'roas',
    label: 'Return on Ad Spend',
    shortLabel: 'ROAS',
    unit: 'x',
    category: 'conversion',
    higherIsBetter: true,
    description:
      'Revenue generated per dollar of ad spend. ' +
      'The primary efficiency metric for performance campaigns.',
  },
  {
    id: 'costPerPurchase',
    label: 'Cost per Purchase',
    shortLabel: 'CPP',
    unit: '$',
    category: 'conversion',
    higherIsBetter: false,
    description: 'Average cost to generate one purchase. Lower = more efficient conversion.',
  },

  // -------------------------------------------------------------------------
  // Cost (5)
  // -------------------------------------------------------------------------
  {
    id: 'costPerThruplay',
    label: 'Cost per ThruPlay',
    shortLabel: 'CPThruPlay',
    unit: '$',
    category: 'cost',
    higherIsBetter: false,
    description: 'Average cost to deliver one ThruPlay. Lower = more efficient at holding attention.',
  },
  {
    id: 'costPerLinkClick',
    label: 'Cost per Link Click',
    shortLabel: 'CPLC',
    unit: '$',
    category: 'cost',
    higherIsBetter: false,
    description: 'Average cost per link click to the destination URL.',
  },
  {
    id: 'costPerLandingPageView',
    label: 'Cost per Landing Page View',
    shortLabel: 'CPLPV',
    unit: '$',
    category: 'cost',
    higherIsBetter: false,
    description: 'Average cost per landing page view.',
  },
  {
    id: 'costPerEngagement',
    label: 'Cost per Engagement',
    shortLabel: 'CPE',
    unit: '$',
    category: 'cost',
    higherIsBetter: false,
    description: 'Average cost per post engagement action.',
  },
  {
    id: 'costPerAddToCart',
    label: 'Cost per Add to Cart',
    shortLabel: 'CPATC',
    unit: '$',
    category: 'cost',
    higherIsBetter: false,
    description: 'Average cost to generate one add-to-cart event.',
  },

  // -------------------------------------------------------------------------
  // Derived (3)
  // -------------------------------------------------------------------------
  {
    id: 'thumbStopRatio',
    label: 'Thumb Stop Ratio',
    shortLabel: 'Thumb Stop',
    unit: '%',
    category: 'derived',
    higherIsBetter: true,
    description:
      '3-second video views divided by total impressions. ' +
      'Measures how effectively the creative stops the scroll.',
  },
  {
    id: 'hookRate',
    label: 'Hook Rate',
    shortLabel: 'Hook Rate',
    unit: '%',
    category: 'derived',
    higherIsBetter: true,
    description:
      '3-second video views divided by total impressions. ' +
      'Measures how effectively the first 3 seconds capture attention.',
  },
  {
    id: 'holdRate',
    label: 'Hold Rate',
    shortLabel: 'Hold Rate',
    unit: '%',
    category: 'derived',
    higherIsBetter: true,
    description:
      'Percentage of 3-second viewers who continue to watch beyond 3 seconds. ' +
      'Measures sustained engagement after the hook.',
  },
];

// ---------------------------------------------------------------------------
// Quick lookup by KPI ID
// ---------------------------------------------------------------------------

export const KPI_BY_ID: Record<string, KpiOption> = KPI_OPTIONS.reduce<Record<string, KpiOption>>(
  (acc, kpi) => {
    acc[kpi.id] = kpi;
    return acc;
  },
  {},
);
